import { setCanDropCalendarItem } from '../mutators/setCanDropMutator';
import { logUsage } from 'owa-analytics';
import { getCalculatedFolderIdForDefaultCalendar } from 'owa-calendar-cache';
import { canEdit, isMeetingOrganizer } from 'owa-calendar-event-capabilities';
import { newCalendarEvent, updateCalendarEvent } from 'owa-calendar-events-actions';
import initializeCalendarEvent from 'owa-calendar-forms-common/lib/utils/initializeCalendarEvent';
import { DroppableType } from 'owa-calendar-surface-dategrid';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { addDays, addMinutes, compare, DisplayDate } from 'owa-datetime';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { getGuid } from 'owa-guid';
import { launchCalendarFullCompose } from 'owa-calendar-forms-launch-handlers';
import {
    TODO_ITEM_DRAG_DATAPOINT_NAME,
    TODO_ITEM_DRAG_ERROR_DATAPOINT_NAME,
    TodoItemDragData,
    getTodoItemDragTelemetryData,
} from 'owa-dnd/lib/utils/TodoItemDragData';
import {
    resumeNotificationsProcessing,
    suspendNotificationsProcessing,
} from 'owa-calendar-events-store';

const SURFACE_VIEW_TYPE = 'Surface';
const DEFAULT_EVENT_DURATION_IN_MINUTES = 30;

/**
 * Whether the given item can be dragged
 * @param item The surface item model
 */
export function canDragItem(item: CalendarEvent): boolean {
    return canEdit(item);
}

/**
 * Whether the specified itemtype can be dropped on the surface.
 * @param dragItemType The itemType of the item being dragged onto the surface.
 */
export function canDropItemType(dragItemType: string, droppableCalendarFolderId: string): boolean {
    const { itemType, itemCalendarFolderId } = getDragItemInfo(dragItemType);

    const wrongCalendarFolderId =
        itemCalendarFolderId &&
        droppableCalendarFolderId &&
        itemCalendarFolderId != droppableCalendarFolderId;
    const returnValue =
        !wrongCalendarFolderId &&
        (itemType == DraggableItemTypes.CalendarItem ||
            itemType == DraggableItemTypes.Todo ||
            itemType == DraggableItemTypes.LocalFile);

    setCanDropCalendarItem(returnValue);
    return returnValue;
}

/**
 * Callback for getting the item to show on surface during a drop operation.
 * This is needed when dragging items that didn't start on the surface,
 * so we don't have an item to start with.
 * @param dragInfo The dragInfo of the item being dragged
 * @param dragStartDate The DisplayDate corresponding to the point where the dragged item
 * entered the calendar surface
 * @param droppableType DroppableType of the region of the calendar surface
 */
export function getSurfaceItemForPreview(
    dragInfo: DragData,
    initialDateTime: DisplayDate,
    droppableType: DroppableType
): CalendarEvent {
    // Generate a new item.
    let previewId = getGuid();
    let isAllDayEvent = droppableType != DroppableType.DayWeek;

    let previewItem = {
        ItemId: { Id: previewId },
        ParentFolderId: { Id: getCalculatedFolderIdForDefaultCalendar() },
        IsAllDayEvent: isAllDayEvent,
        Start: initialDateTime,
        End: isAllDayEvent
            ? addDays(initialDateTime, 1)
            : addMinutes(initialDateTime, DEFAULT_EVENT_DURATION_IN_MINUTES),
        ClientSeriesId: previewId,
    } as CalendarEvent;

    const { itemType } = getDragItemInfo(dragInfo.itemType);

    if (itemType == DraggableItemTypes.Todo) {
        // when both drag item and drop target are in same JS context / HTML document,
        // we can access additional details during drag operations via itemData property
        const { itemData } = dragInfo as TodoItemDragData;

        // attempt to set more meaningful properties, but fall back to single-space string
        // if subject is not available (e.g. due to cross-window in OPX scenarios) so that
        // we show no subject rather than default "(No subject)" string
        //
        // even though `itemData` is marked as required on `TodoItemDragData` interface,
        // we need to treat as optional when accessed during drag phase to account for
        // cross-frame scenario where we can't actually access the additional details
        previewItem.Subject = itemData?.subject || ' ';
        previewItem.CharmId = 55; // Checkmark charm
    }

    return previewItem;
}

/**
 * Called when the item is dropped in one of the droppable areas of the surface.
 * @param droppedItem The dropped item with modified time and all-day flag, if needed
 * @param originalItem The original item prior to updates
 * @param dragInfo The DragData for the drag-drop operation
 * @param handleEditOnItemDrop optional edit handler to override the default popout behavior
 */
export function onItemDropped(
    droppedItem: CalendarEvent,
    originalItem: CalendarEvent,
    dragInfo: DragData,
    handleEditOnItemDrop?: (event: Partial<CalendarEvent>, eventHasUpdates: boolean) => void
) {
    const { itemType } = getDragItemInfo(dragInfo.itemType);
    const calendarEvent = droppedItem;

    if (itemType == DraggableItemTypes.CalendarItem) {
        // check if the item has actually changed, if not do nothing
        if (
            compare(calendarEvent.Start, originalItem.Start) === 0 &&
            compare(calendarEvent.End, originalItem.End) === 0 &&
            calendarEvent.IsAllDayEvent == originalItem.IsAllDayEvent
        ) {
            return Promise.resolve();
        }

        const calendarItemUpdates: CalendarEvent = {
            ItemId: calendarEvent.ItemId,
            Start: calendarEvent.Start,
            End: calendarEvent.End,
            IsAllDayEvent: calendarEvent.IsAllDayEvent,
        } as CalendarEvent;

        if (isMeetingOrganizer(calendarEvent)) {
            if (calendarItemUpdates.IsAllDayEvent) {
                // Additional update needed for meetings to ensure the all day event date is adjusted for the meeting form
                calendarItemUpdates.End = addDays(calendarItemUpdates.End, -1);
            }
            if (handleEditOnItemDrop) {
                handleEditOnItemDrop(calendarItemUpdates, true /* eventHasUpdates */);
            } else {
                launchCalendarFullCompose({
                    fullComposeEntrySource: SURFACE_VIEW_TYPE,
                    eventUpdates: calendarItemUpdates,
                    itemHasUpdates: true,
                });
            }
        } else {
            updateCalendarEvent(calendarEvent, calendarItemUpdates, SURFACE_VIEW_TYPE);
        }
    } else if (itemType == DraggableItemTypes.Todo) {
        const dragData = dragInfo as TodoItemDragData;
        const { itemData } = dragData;

        // Construct the calendar event to be constructed
        const taskEvent = initializeCalendarEvent({
            ...calendarEvent,
            // overwrite subject from preview model in case of empty subject
            // due to cross-window drag-and-drop scenario
            Subject: itemData.subject,
        });

        // Create new with local lies.
        // Return promise so that the datapoint include creation of new event
        return new Promise((resolve, reject) => {
            const onSuccess = (event: CalendarEvent) => {
                const customData = { ...getTodoItemDragTelemetryData(dragData) };
                logUsage(TODO_ITEM_DRAG_DATAPOINT_NAME, customData);
                resolve(event);
            };
            const onError = (error: Error) => {
                const customData = { ...getTodoItemDragTelemetryData(dragData) };
                logUsage(TODO_ITEM_DRAG_ERROR_DATAPOINT_NAME, customData);
                reject(error);
            };
            newCalendarEvent(taskEvent, SURFACE_VIEW_TYPE, onSuccess, onError);
        });
    }
    return Promise.resolve();
}

/**
 * Called when the user starts dragging an item
 * @param item The item being dragged
 */
export function onDragStart(item: CalendarEvent) {
    suspendNotificationsProcessing();
}

/**
 * Called when the user ends dragging an item by dropping it inside or outside a droppable area.
 * @param item The item being dragged
 */
export function onDragEnd(item: CalendarEvent) {
    resumeNotificationsProcessing();
    setCanDropCalendarItem(false);
}

function getDragItemInfo(dragItemType: string) {
    let itemType = dragItemType;
    let itemCalendarFolderId = null;

    // this is a temporary solution until Droppable and Draggle components are refactored to handle containerId in DragData
    // tracked by VSO #33820
    if (dragItemType && dragItemType.indexOf('_') > 0) {
        let splitStrings = dragItemType.split('_');
        itemType = splitStrings[0];
        itemCalendarFolderId = splitStrings[1];
    }
    return { itemType, itemCalendarFolderId };
}
